import {
    Directive,
    ElementRef,
    Input,
    Output,
    AfterContentInit,
    NgModule,
    EventEmitter,
    OnDestroy,
    HostListener,
    Optional,
    Self,
    OnChanges,
    SimpleChanges,
    Renderer2,
    OnInit
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgControl } from '@angular/forms';
import { Subscription } from 'rxjs';

/* tslint:disable */
@Directive({
    selector: `
    input[euiMaxlength],
    textarea[euiMaxlength],
    [ngModel][euiMaxlength],
    [formControlName][euiMaxlength]`,
})
export class EuiMaxLengthDirective implements OnInit, AfterContentInit, OnDestroy, OnChanges {
    @Input('euiMaxlength') maxLength: number;
    @Input('isShowMaxlength') isShowMaxlength: boolean = true;
    @Output() maxLengthReached: EventEmitter<boolean> = new EventEmitter<boolean>();

    valueChangesSubscription: Subscription;

    private maxLengthSpan: HTMLSpanElement;
    private wrapper: HTMLDivElement;
    private lengthCalcFn: () => number;

    constructor(private el: ElementRef,
                private renderer: Renderer2,
                @Self() @Optional() private control: NgControl) {
    }

    ngOnInit(): void {
        // set default length calculation behavior
        this.setLengthCalcFactory((value) => value?.length || 0);
    }

    ngOnDestroy() {
        // remove all DOM added elements
        this.destroyMaxLengthBox();

        // unsubscribe from valueChanges
        if(this.valueChangesSubscription) {
            this.valueChangesSubscription.unsubscribe();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if(changes['isShowMaxlength'] && this.maxLengthSpan) {
            if(this.isShowMaxlength) {
                this.createMaxLengthBox();
                this._refreshValue();
            } else {
                this.destroyMaxLengthBox();
            }
        }

        if(changes['length']) {
            if(this.maxLength < 0) {
                throw new Error('Length of an input or a textarea cannot be negative.')
            }
            this._refreshValue();
        }
    }

    ngAfterContentInit() {
        if (this.maxLength) {
            this.createMaxLengthBox();
            this._refreshValue();
        }

        // this subscription is needed apart from HostListener to Changes in case usage of this directive
        // in custom component that implements ngDefaultControl
        if (this.control?.valueChanges) {
            this.valueChangesSubscription = this.control.valueChanges.subscribe(() => {
                this._refreshValue();
            });
        }
    }

    @HostListener('change', ['$event'])
    onChange(event: Event) {
        if (!this.control) {
            this._refreshValue();
        }
    }

    @HostListener('input', ['$event'])
    onInput(event: KeyboardEvent) {
        if (!this.control) {
            this._refreshValue();
        }
    }

    @HostListener('paste', ['$event'])
    onPaste(event: ClipboardEvent) {
        // get the value from clipboard
        let value = (event.clipboardData || window['clipboardData']).getData('text');
        // calculate and set the maxLength based on the clipboard value
        let input = <HTMLInputElement>this.el.nativeElement;
        this.renderer.setProperty(input, 'maxLength', this.calculateMaxLength(value));
    }

    public setLengthCalcFactory(fn: (input: string) => number): void {
        this.lengthCalcFn = () => {
            let input = <HTMLInputElement>this.el.nativeElement;
            const value = this.control ? this.control.value : input.value;
            return fn(value);
        }
    }

    /**
     * calculates the maxLength of an input element. Calculation is made
     * against the value of the Model or Input Element.
     * @param value Optional the value to calculate max length against (override the Model one)
     * @private
     */
    private calculateMaxLength(value?: string): number {
        let input = <HTMLInputElement>this.el.nativeElement;
        const inputValue = this.control ? this.control.value : input.value;
        return (value?.length || (inputValue?.length || 0) - this.lengthCalcFn()) + this.maxLength;
    }

    private _refreshValue() {
        let input = <HTMLInputElement>this.el.nativeElement;
        let valueLength = this.lengthCalcFn();
        let remainingLength = this.maxLength - valueLength;

        this.renderer.setProperty(input, 'maxLength', this.calculateMaxLength());
        if(this.maxLengthSpan) {
            // based on the remaining length add/remove the error class
            if (remainingLength <= 0) {
                this.renderer.addClass(this.maxLengthSpan, 'error')
                this.maxLengthReached.emit(true);
            } else {
                this.renderer.removeClass(this.maxLengthSpan, 'error')
                this.maxLengthReached.emit(false);
            }
            // set the value of remaining characters
            if (remainingLength < 0) {
                // this feature is only available when the calculation of length is not custom
                // to support this feature you need to calculate this.maxLength also
                if (this.control) {
                    this.control.control.setValue(input.value.substr(0, this.calculateMaxLength()))
                } else {
                    input.value = input.value.substr(0, this.calculateMaxLength());
                }
                this.renderer.setProperty(this.maxLengthSpan, 'innerHTML', '0')
            } else if (remainingLength >= 0) {
                this.renderer.setProperty(this.maxLengthSpan, 'innerHTML', (this.maxLength - valueLength).toString())
            }
        }
    }

    /**
     * creates the maxLength box by adding it to DOM and attaching correct Classes
     * for Wrapper and Span (actual box holding max length)
     * @private
     */
    private createMaxLengthBox() {
        // creates the span that holds the max length value which changes according to input
        this.maxLengthSpan = this.renderer.createElement('span');
        this.renderer.addClass(this.maxLengthSpan, 'input-maxlength')
        this.renderer.setAttribute(this.maxLengthSpan, 'innerHTML', this.maxLength.toString());

        // create wrapper element
        let nativeElement: HTMLElement = this.el.nativeElement;
        if (nativeElement != null) {
            // set width to 100%
            this.renderer.setStyle(nativeElement, 'width', '100%');
            let parent: Node = nativeElement.parentNode;
            if (parent != null && this.isShowMaxlength) {
                // creates wrapper div of input and maxLength span
                this.wrapper = this.renderer.createElement('div');
                this.renderer.addClass(this.wrapper, 'input-maxlength-wrapper');
                // swap, wrapper should wrap the nativeElement and maxLength span
                parent.replaceChild(this.wrapper, nativeElement);
                this.renderer.appendChild(this.wrapper, nativeElement);
                this.renderer.appendChild(this.wrapper, this.maxLengthSpan);
            }
        }
    }

    /**
     * destroys the maxLength box from DOM
     * @private
     */
    private destroyMaxLengthBox() {
        // removes maxlength element from DOM
        if(this.maxLengthSpan) {
            this.renderer.removeChild(this.maxLengthSpan.parentElement, this.maxLengthSpan);
        }

        // remove wrapper from DOM
        if(this.wrapper) {
            // get parent node of wrapper
            let parent: Node = this.wrapper.parentNode;
            let nativeElement: HTMLElement = this.el.nativeElement;
            // swap wrapper and nativeElement
            parent?.replaceChild(nativeElement, this.wrapper);
            // removes wrapper element from DOM
            this.renderer.removeChild(this.wrapper.parentElement, this.wrapper);
        }
    }

}

@NgModule({
    imports: [CommonModule],
    exports: [EuiMaxLengthDirective],
    declarations: [EuiMaxLengthDirective]
})
export class EuiMaxLengthDirectiveModule {
}
/* tslint:enable */
